<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <!-- Filtres avec boutons -->
    <AssetFilters
      v-model:active-filter="viewFilter"
      title="Lieux"
      asset-label="lieux"
      :stats="locationsStats"
      :is-loading="isLoading"
      @refresh="refreshLocations"
    />

    <div v-if="isLoading" class="loader">
      <div class="spinner"></div>
    </div>

    <template v-else>
      <LocationSubset
        :locations="filteredLocations"
        :show-actions="true"
        :show-edit-button="true"
        :show-delete-button="true"
        @create="openCreationModal"
        @edit="editLocation"
        @delete="handleDeleteLocation"
      />
    </template>

    <!-- Modal de création/édition -->
    <LocationCreator 
      v-model:show="showCreatorModal"
      :mode="creatorMode"
      :initial-data="selectedLocation"
      :user-id="userId"
      @location-created="handleLocationCreated"
      @location-updated="handleLocationUpdated"
      @show-notification="showNotification"
    />
    
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { deleteLocation } from "@/api/locations";
import type { Location, LocationWithMemos } from "@/api/types/entities/location/types";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import Notification from "@/components/Notification.vue";
import AssetFilters from "@/components/UI/AssetFilters.vue";
import LocationCreator from "@/components/creators/LocationCreator.vue";
import LocationSubset from "@/components/subsets/LocationSubset.vue";
import { useLocations } from '@/composables/useAssets';
import { useAuthStore } from '@/stores/auth';
import { useBreadcrumbsStore } from "@/stores/breadcrumbs";
import type { NotificationType } from "@/types/notification";
import { computed, ref } from "vue";

// Stores et composables
const authStore = useAuthStore();
const breadcrumbsStore = useBreadcrumbsStore();
const {
  filteredAssets: filteredLocations,
  isLoading,
  stats: locationsStats,
  viewFilter,
  refreshAssets: refreshLocations,
  addAsset: addLocation,
  updateAsset: updateLocation,
  removeAsset: removeLocation
} = useLocations({ autoLoad: true }) as any; // TODO: Adapter le composable pour LocationWithMemos



// État local
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showCreatorModal = ref(false);
const creatorMode = ref<'create' | 'edit'>('create');
const selectedLocation = ref<Location | undefined>(undefined);

// Computed
const userId = computed((): string => authStore.user?.uuid || '');

// Initialiser les breadcrumbs
breadcrumbsStore.updateBreadcrumbs([
  { name: 'Outils', path: '/dashboard' },
  { name: 'Mes Lieux', path: '/mylocations' }
]);

// Méthodes
const openCreationModal = (): void => {
  selectedLocation.value = undefined;
  creatorMode.value = 'create';
  showCreatorModal.value = true;
};

// Fonction utilitaire pour convertir une LocationWithMemos en Location
function toLocation(locationWithMemos: LocationWithMemos): Location {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { memos, material_entities, ...location } = locationWithMemos;
  return location as Location;
}

const editLocation = (location: LocationWithMemos): void => {
  selectedLocation.value = toLocation(location);
  creatorMode.value = 'edit';
  showCreatorModal.value = true;
};

const handleLocationCreated = (location: Location): void => {
  addLocation(location);
  showNotification("Emplacement créé avec succès", "success");
};

const handleLocationUpdated = (location: Location): void => {
  updateLocation(location);
  showNotification("Emplacement mis à jour avec succès", "success");
};

const handleDeleteLocation = async (location: LocationWithMemos): Promise<void> => {
  try {
    await deleteLocation(location.uuid);
    removeLocation(location.uuid);
    showNotification("Emplacement supprimé avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression de l'emplacement:", error);
    showNotification("Erreur lors de la suppression de l'emplacement", "error");
  }
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};
</script>

<style>
.input-edit {
  display: block;
  width: 100%;
  padding: 0.75rem 0.75rem;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.input-edit:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 1px #0ea5e9;
}

.loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 